import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Code, User, BookOpen, Mail, Github, Linkedin } from "lucide-react";
import brimericaLogo from "./assets/brimerica-noBG.png";

export default function Layout({ children }) {
    const location = useLocation();

    const navigationItems = [
        { name: "Home", path: "/", icon: Code },
        { name: "About", path: "/about", icon: User },
        { name: "Articles", path: "/articles", icon: BookOpen },
        { name: "Contact", path: "/contact", icon: Mail },
    ];

    return (
        <div className="min-h-screen bg-slate-900 text-slate-300">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <Link
                            to="/"
                            className="flex items-center space-x-2 group"
                        >
                            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <img
                                    src={brimericaLogo}
                                    alt="Brimerica Logo"
                                    className="w-7 h-7"
                                    style={{ objectFit: "contain" }}
                                />
                            </div>
                            <span className="text-xl font-bold text-white">
                                Brimerica
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center space-x-8">
                            {navigationItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.path}
                                        className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${isActive
                                            ? 'bg-slate-800 text-cyan-400'
                                            : 'text-slate-300 hover:text-cyan-300 hover:bg-slate-800'
                                            }`}
                                    >
                                        <item.icon className="w-4 h-4" />
                                        <span className="font-medium">{item.name}</span>
                                    </Link>
                                );
                            })}
                        </div>

                        <div className="flex items-center space-x-4">
                            <a
                                href="https://github.com/Pb-22"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-slate-400 hover:text-cyan-400 transition-colors duration-300"
                            >
                                <Github className="w-5 h-5" />
                            </a>
                            <a
                                href="https://www.linkedin.com/in/brian-williams-cyber-professional/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-slate-400 hover:text-cyan-400 transition-colors duration-300"
                            >
                                <Linkedin className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20">
                {children}
            </main>

            {/* Footer */}
            <footer className="bg-slate-900/50 border-t border-slate-800 text-white py-12 mt-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid md:grid-cols-3 gap-8">
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                                    <Code className="w-4 h-4 text-white" />
                                </div>
                                <span className="text-xl font-bold">Brimerica</span>
                            </div>
                            <p className="text-slate-400">
                                Showcasing innovation in cybersecurity through code, creativity, and continuous learning.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-slate-200">Navigation</h3>
                            <div className="space-y-2">
                                {navigationItems.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.path}
                                        className="block text-slate-400 hover:text-white transition-colors duration-300"
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-slate-200">Connect</h3>
                            <div className="flex space-x-4">
                                <a
                                    href="https://github.com/Pb-22"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors duration-300"
                                >
                                    <Github className="w-5 h-5" />
                                    <span>GitHub</span>
                                </a>
                                <a
                                    href="https://www.linkedin.com/in/brian-williams-cyber-professional/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors duration-300"
                                >
                                    <Linkedin className="w-5 h-5" />
                                    <span>LinkedIn</span>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
                        <p>&copy; 2024 Brimerica. Crafted with passion for technology.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
